import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createEventSchema,
  type CreateEventInput,
} from '@wingseat/shared';
import { useCreateEvent } from '../hooks/use-events';
import { ApiError } from '../lib/api-client';
import { fromDateTimeLocal, toDateTimeLocal } from '../lib/format';

function defaultStart(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setHours(18, 0, 0, 0);
  return toDateTimeLocal(d);
}

export function NewEventPage() {
  const navigate = useNavigate();
  const create = useCreateEvent();

  const [name, setName] = useState('');
  const [startAt, setStartAt] = useState(defaultStart());
  const [hasEnd, setHasEnd] = useState(false);
  const [endAt, setEndAt] = useState(defaultStart());
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const input: CreateEventInput = {
      name: name.trim(),
      startAt: fromDateTimeLocal(startAt),
      endAt: hasEnd ? fromDateTimeLocal(endAt) : null,
      location: location.trim(),
      description: description.trim() || null,
    };
    const parsed = createEventSchema.safeParse(input);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const iss of parsed.error.issues) {
        const k = iss.path[0]?.toString();
        if (k && !errs[k]) errs[k] = iss.message;
      }
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    create.mutate(parsed.data, {
      onSuccess: (data) => {
        navigate(`/app/events/${data.id}`, { replace: true });
      },
    });
  };

  const apiErr =
    create.error instanceof ApiError
      ? create.error.message
      : create.error
        ? 'Anlegen fehlgeschlagen'
        : null;

  return (
    <form onSubmit={onSubmit} className="px-4 py-4 space-y-4">
      <h1 className="text-xl font-semibold">Neues Event</h1>

      <Field label="Name" error={fieldErrors['name']}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Spätsommerfest"
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </Field>

      <Field label="Startdatum + Uhrzeit" error={fieldErrors['startAt']}>
        <input
          type="datetime-local"
          value={startAt}
          onChange={(e) => setStartAt(e.target.value)}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </Field>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={hasEnd}
            onChange={(e) => setHasEnd(e.target.checked)}
            className="h-4 w-4 rounded border-slate-600 bg-slate-950 accent-brand-600"
          />
          Enddatum / -uhrzeit angeben
        </label>
        {hasEnd && (
          <Field label="Ende" error={fieldErrors['endAt']}>
            <input
              type="datetime-local"
              value={endAt}
              onChange={(e) => setEndAt(e.target.value)}
              className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
            />
          </Field>
        )}
      </div>

      <Field label="Ort" error={fieldErrors['location']}>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="z. B. EDAZ Schönhagen"
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </Field>

      <Field label="Beschreibung (optional)" error={fieldErrors['description']}>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-base"
        />
      </Field>

      {apiErr && (
        <div className="rounded-lg border border-rose-900 bg-rose-950/60 px-3 py-2 text-sm text-rose-200">
          {apiErr}
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={create.isPending}
          className="flex-1 rounded-lg bg-brand-600 px-4 py-3 font-medium text-white hover:bg-brand-700 disabled:opacity-60"
        >
          {create.isPending ? 'Wird gespeichert …' : 'Event anlegen'}
        </button>
        <button
          type="button"
          onClick={() => navigate('/app/events')}
          className="rounded-lg border border-slate-700 px-4 py-3 text-sm"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-400">{label}</label>
      {children}
      {error && <p className="text-xs text-rose-400">{error}</p>}
    </div>
  );
}
